import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  ArrowLeft, Calendar, Rocket, ClipboardList, Plus, FileText,
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import useBranchLabels from '../../hooks/useBranchLabels';
import DatesheetEditor from './DatesheetEditor';
import { resultsEntryButtonLabel } from '../../utils/examResultsLabels';

const statusBadge = (status) => {
  const map = {
    none: 'bg-muted text-muted-foreground',
    draft: 'bg-amber-500/20 text-amber-400',
    published: 'bg-green-500/20 text-green-400',
  };
  return map[status] || map.none;
};

const ExamDetail = () => {
  const { examId } = useParams();
  const navigate = useNavigate();
  const { user, canManageFeature } = useAuth();
  const canManage = canManageFeature('exams');
  const { formatLabel } = useBranchLabels();
  const { toast } = useToast();

  const [exam, setExam] = useState(null);
  const [datesheets, setDatesheets] = useState([]);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [launching, setLaunching] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorClass, setEditorClass] = useState(null);
  const [newClassId, setNewClassId] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [examRes, dsRes] = await Promise.all([
        api.get(`/exams/${examId}`),
        api.get(`/exams/${examId}/datesheets`),
      ]);
      setExam(examRes.data.data);
      setDatesheets(dsRes.data.data || []);
      if (canManage) {
        const academicYearId = user?.school_id
          ? localStorage.getItem(`selected_academic_year_id_${user.school_id}`)
          : null;
        const params = academicYearId ? { academic_year_id: academicYearId } : {};
        const clsRes = await api.get('/classes', { params });
        setClasses(clsRes.data.data || []);
      }
    } catch {
      toast({ title: 'Error', description: 'Failed to load exam', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [examId, canManage, user?.school_id, toast]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openEditor = (cls) => {
    setEditorClass(cls);
    setEditorOpen(true);
  };

  const openNewDatesheet = () => {
    if (!newClassId) {
      toast({ title: 'Select a class first', variant: 'destructive' });
      return;
    }
    const cls = classes.find((c) => String(c.id) === newClassId);
    openEditor({ class_id: Number(newClassId), class_name: cls?.name || 'Class' });
  };

  const handleLaunch = async () => {
    if (!window.confirm('Launch datesheet for the selected class(es)? Students and parents in those classes will be notified.')) return;
    setLaunching(true);
    try {
      await api.post(`/exams/${examId}/launch-datesheet`);
      toast({ title: 'Success', description: 'Datesheet launched — notifications sent' });
      fetchData();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.message || 'Launch failed', variant: 'destructive' });
    } finally {
      setLaunching(false);
    }
  };

  const handlePublishResults = async () => {
    if (!window.confirm('Publish results for all students? They will be notified.')) return;
    setPublishing(true);
    try {
      await api.post(`/exams/${examId}/publish-results`);
      toast({ title: 'Success', description: 'Results published' });
      fetchData();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.message || 'Publish failed', variant: 'destructive' });
    } finally {
      setPublishing(false);
    }
  };

  const examTypes = {
    term: 'Term Exam', mid_term: 'Mid-Term', final: 'Final Exam', unit_test: 'Unit Test', other: 'Other',
  };

  const datesheetPublished = exam?.datesheet_status === 'published';
  const readOnly = !canManage || datesheetPublished;
  const visibleDatesheets = canManage ? datesheets : datesheets.filter((d) => d.status === 'published');

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (!exam) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        Exam not found. <Link to="/exams" className="text-blue-400">Back to exams</Link>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="exam-detail-page">
      <div className="flex items-start gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/exams')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-foreground">{exam.name}</h1>
          <p className="text-muted-foreground">{exam.description || examTypes[exam.exam_type]}</p>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <Calendar className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              {new Date(exam.start_date).toLocaleDateString()} – {new Date(exam.end_date).toLocaleDateString()}
            </span>
            <Badge className={statusBadge(exam.datesheet_status || 'none')}>
              Datesheet: {exam.datesheet_status || 'none'}
            </Badge>
            <Badge className={statusBadge(exam.results_status || 'none')}>
              Results: {exam.results_status || 'none'}
            </Badge>
          </div>
        </div>
        {canManage && !datesheetPublished && datesheets.some((d) => d.status === 'draft' && Number(d.schedule_count) > 0) && (
          <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleLaunch} disabled={launching}>
            <Rocket className="w-4 h-4 mr-2" />
            {launching ? 'Launching…' : 'Launch Datesheet'}
          </Button>
        )}
      </div>

      <Card className="border-border">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="w-5 h-5" /> Class Datesheets
          </CardTitle>
          {canManage && !datesheetPublished && (
            <div className="flex items-center gap-2">
              <Select value={newClassId} onValueChange={setNewClassId}>
                <SelectTrigger className="w-40 bg-muted border-border h-9"><SelectValue placeholder="Add class" /></SelectTrigger>
                <SelectContent className="bg-muted border-border">
                  {classes
                    .filter((c) => !datesheets.some((d) => Number(d.class_id) === Number(c.id)))
                    .map((c) => <SelectItem key={c.id} value={String(c.id)}>{formatLabel(c.name, c)}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button size="sm" variant="outline" onClick={openNewDatesheet}>
                <Plus className="w-4 h-4 mr-1" /> Add
              </Button>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {visibleDatesheets.length === 0 ? (
            <p className="text-muted-foreground text-center py-6">
              {canManage ? 'No datesheets yet. Add a class datesheet above.' : 'Datesheet not published yet.'}
            </p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2">
              {visibleDatesheets.map((ds) => (
                <button
                  key={ds.id}
                  type="button"
                  onClick={() => openEditor({ class_id: ds.class_id, class_name: ds.class_name })}
                  className="text-left border border-border rounded-lg p-4 hover:border-blue-500/50 transition-colors"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-foreground">{ds.class_name}</span>
                    <Badge className={statusBadge(ds.status)}>{ds.status}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {ds.schedule_count || 0} subject(s)
                    {ds.date_from && ds.date_to && (
                      <> · {new Date(ds.date_from).toLocaleDateString()} – {new Date(ds.date_to).toLocaleDateString()}</>
                    )}
                  </p>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {(canManage || exam.results_status === 'published') && datesheetPublished && (
        <Card className="border-border">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <ClipboardList className="w-5 h-5" /> Results
            </CardTitle>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-3">
            {canManage && (
              <>
                <Button onClick={() => navigate(`/exams/${examId}/results`)} className="bg-blue-600 hover:bg-blue-700">
                  {resultsEntryButtonLabel(exam)}
                </Button>
                <Button variant="outline" onClick={() => navigate(`/exams/${examId}/results/analytics`)}>
                  View Analytics
                </Button>
                {exam.results_status === 'draft' && (
                  <Button variant="outline" onClick={handlePublishResults} disabled={publishing}>
                    {publishing ? 'Publishing…' : 'Publish Results'}
                  </Button>
                )}
              </>
            )}
            {!canManage && exam.results_status === 'published' && (
              <>
                <Button variant="outline" onClick={() => navigate(`/exams/${examId}/results/analytics`)}>
                  View Analytics
                </Button>
                <p className="text-sm text-muted-foreground">Results have been published for this exam.</p>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {canManage && !datesheetPublished && (
        <p className="text-sm text-muted-foreground">
          Save datesheets for each class, then use <strong>Launch Datesheet</strong> to notify students and parents.
        </p>
      )}

      <DatesheetEditor
        open={editorOpen}
        onOpenChange={setEditorOpen}
        examId={examId}
        classId={editorClass?.class_id}
        className={editorClass?.class_name}
        readOnly={readOnly}
        onSaved={fetchData}
      />
    </div>
  );
};

export default ExamDetail;
